import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, List, Modal } from "antd";
import { Rule } from "antd/es/form";
import { DefaultOptionType } from "antd/es/select";
import {
  patternRFC,
  patternRfcPersonaFisica,
} from "services/utils/rfcValidator";

export function ExtraInfoBtn(
  title: string,
  content: string[]
): React.ReactNode {
  return (
    <Button
      type="text"
      icon={<InfoCircleOutlined />}
      onClick={() => {
        Modal.info({
          title,
          content: (
            <List>
              {content.map((line) => {
                return <List.Item key={line}>{line}</List.Item>;
              })}
            </List>
          ),
        });
      }}
    />
  );
}

export function FilterSelectOpts(input: string, option?: DefaultOptionType) {
  return option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
}

/** Validaciones */
export const RFCRules = [
  {
    required: true,
    message: "Por favor introduce el RFC del contribuyente.",
  },
  { min: 12, message: "Un RFC válido tiene al menos 12 caracteres." },
  { max: 13, message: "Un RFC válido tiene no más de 13 caracteres." },
  {
    pattern: patternRFC,
    message: "El RFC tiene un formato no válido.",
  },
];

export const RFCPersonaFisicaRules = [
  {
    required: true,
    message: "Por favor introduce el RFC del representante legal.",
  },
  {
    min: 13,
    message: "Un RFC Persona Física válido tiene exactamente 13 caracteres.",
  },
  {
    max: 13,
    message: "Un RFC Persona Física válido tiene exactamente 13 caracteres.",
  },
  {
    pattern: patternRfcPersonaFisica,
    message: "Introduce un RFC Persona Física válido.",
  },
];

const NameRules = [
  {
    required: true,
    message: "Introduce el nombre o razón social del contribuyente.",
  },
];

const NameRepresentanteLegatRules = [
  {
    required: true,
    message: "Introduce el RFC del representante legal.",
  },
];

const SubsidiaryRules = [
  {
    required: true,
    message: "Introduce el nombre del punto de distribución",
  },
];

const CompanyActivityTypeRules = [
  {
    required: true,
    message: "Por favor selecciona el tipo de actividad realizada",
  },
];

const InstallationKeyRules = [
  {
    required: true,
    message: "Por favor selecciona la clave de instalación de la planta.",
  },
];

const InstallationKeyNumerRules = [
  {
    required: true,
    message: "Por favor introduce el número de la clave de instalación.",
  },
  {
    pattern: /^[0-9-]{4}$/,
    message: "Introduce un valor de 4 caracteres, puedes usar sólo números.",
  },
];

const OperationLicenseRules = [
  {
    required: true,
    message:
      "Por favor introduce el número de oficio del aviso de inicio de operaciones.",
  },
];

const TimeZoneRules = [
  {
    required: true,
    message: "Por favor selecciona una zona horaria.",
  },
];

const SenerLicenseRules = [
  {
    required: true,
    message:
      "Por favor introduce el número de permiso otorgado por la CRE o la Secretaría de Energía.",
  },
];

const DescriptionRules = [
  {
    required: true,
    message: "Por favor introduce una descripción.",
  },
];

const CharacterRules = [
  {
    required: true,
    message:
      "Por favor selecciona el carácter con el que actúa para efectos regulatorios.",
  },
];

const ContractAsignationNumberRules = [
  {
    required: true,
    message:
      "Por favor introduce el número de asignación otorgada por la Secretaría de Energía o el número de contrato celebrado con la Comisión Nacional de Hidrocarburos.",
  },
  { min: 14, message: "Introduce mínimo 14 caracteres." },
  { max: 24, message: "Introduce máximo 24 caracteres." },
];

const PermissionModalityRules = [
  {
    required: true,
    message: "Por favor selecciona la modalidad del permiso.",
  },
];

const NaturalGasStorageInstalationRules = [
  {
    required: true,
    message: "Por favor describe la instalación del almacén.",
  },
  { min: 16, message: "Introduce mínimo 16 caracteres." },
  { max: 250, message: "Introduce máximo 250 caracteres." },
];

const StreetRules = [
  {
    required: true,
    message: "Por favor introduce el nombre de la calle.",
  },
];

const StreetNumberRules = [
  {
    required: true,
    message: "Por favor introduce el número de la calle.",
  },
];

const StreetInsideNumberRules = [
  { max: 10, message: "Introduce máximo 10 caracteres." },
];

const ColonyRules = [
  {
    required: true,
    message: "Por favor introduce el nombre de la colonia.",
  },
];

const ZipCodeRules = [
  {
    required: true,
    message: "Por favor introduce el código postal.",
  },
  {
    pattern: /^\d{5}$/,
    message: "Un código postal válido tiene exactamente 5 digitos.",
  },
];

const StateRules = [
  {
    required: true,
    message: "Por favor introduce el Estado.",
  },
];

const MunicipalityRules = [
  {
    required: true,
    message: "Por favor introduce el municipio.",
  },
];

const LatitudeRules: Rule[] = [
  {
    required: true,
    message:
      "Por favor introduce la latitud de la ubicación de la planta o haz click en el mapa en donde se encuentra ubicada.",
  },
  {
    validator(_: Rule, value: number) {
      const numberVal = typeof value === "string" ? parseFloat(value) : value;

      if (typeof numberVal !== "number" || isNaN(numberVal)) {
        return Promise.reject("Introduce una latitud válida.");
      }
      if (numberVal < -90 || numberVal > 90) {
        return Promise.reject("La latitud debe estar entre -90 y 90.");
      }
      return Promise.resolve();
    },
  },
];

const LongitudeRules: Rule[] = [
  {
    required: true,
    message:
      "Por favor introduce la longitud de la ubicación de la planta o haz click en el mapa en donde se encuentra ubicada.",
  },
  {
    validator(_: Rule, value: number) {
      const numberVal = typeof value === "string" ? parseFloat(value) : value;

      if (typeof numberVal !== "number" || isNaN(numberVal)) {
        return Promise.reject("Introduce una longitud válida.");
      }
      if (numberVal < -180 || numberVal > 180) {
        return Promise.reject("La longitud debe estar entre -180 y 180.");
      }
      return Promise.resolve();
    },
  },
];

const ProductsSoldRules = [
  {
    required: true,
    message: "Por favor introduce los productos a vender.",
  },
];

const MaxDifferencePercentageRules: Rule[] = [
  {
    required: true,
    message: "Por favor introduce introduce una diferencia máxima.",
  },
  {
    validator(_: Rule, value: number) {
      if (typeof value !== "number" || isNaN(value)) {
        return Promise.reject(
          "Introduce una diferencia númerica máxima válida."
        );
      }
      if (value < 0.1) {
        return Promise.reject(
          "El valor porcentaje de diferencia debe ser de cuando menos 0.1"
        );
      }
      if (value > 10) {
        return Promise.reject(
          "El valor porcentaje de diferencia debe ser menor a 10"
        );
      }
      return Promise.resolve();
    },
  },
];

const EmailsRules = [
  {
    required: true,
    message: "Por favor introduce al menos un email",
  },
];

export default {
  ExtraInfoBtn,
  FilterSelectOpts,
  RFCRules,
  NameRules,
  SubsidiaryRules,
  NameRepresentanteLegatRules,
  RFCPersonaFisicaRules,
  CompanyActivityTypeRules,
  InstallationKeyRules,
  InstallationKeyNumerRules,
  OperationLicenseRules,
  TimeZoneRules,
  SenerLicenseRules,
  DescriptionRules,
  CharacterRules,
  ContractAsignationNumberRules,
  PermissionModalityRules,
  NaturalGasStorageInstalationRules,
  StreetRules,
  StreetInsideNumberRules,
  StreetNumberRules,
  ColonyRules,
  ZipCodeRules,
  StateRules,
  MunicipalityRules,
  LatitudeRules,
  LongitudeRules,
  ProductsSoldRules,
  MaxDifferencePercentageRules,
  EmailsRules,
};
